import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {DateField} from '@/components/date-field'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {NoPsicologosNotice} from '@/components/bateria/no-psicologos-notice'
import {PsicologoSelect} from '@/components/bateria/psicologo-select'
import {useCreateEvaluacion, useVerifiedPsicologos} from '@/hooks/bateria/use-bateria'
import {translateApiError} from '@/lib/errors'

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

export function NuevaEvaluacionDialog({
  businessId,
  open,
  onOpenChange,
  onCreated,
}: {
  businessId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (evaluacionId: string) => void
}) {
  const {t} = useTranslation()
  const psicologos = useVerifiedPsicologos(open)
  const create = useCreateEvaluacion(businessId)
  const [psicologoUserId, setPsicologoUserId] = useState('')
  const [fecha, setFecha] = useState(todayIso())
  const [notas, setNotas] = useState('')
  const [error, setError] = useState<string | null>(null)

  function close() {
    setPsicologoUserId('')
    setFecha(todayIso())
    setNotas('')
    setError(null)
    create.reset()
    onOpenChange(false)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!psicologoUserId) {
      setError(t('admin.bateria.nuevaDialog.psicologoRequired'))
      return
    }
    create.mutate(
      {fecha_inicio: fecha, psicologo_user_id: psicologoUserId, notas: notas.trim() || null},
      {
        onSuccess: (evaluacion) => {
          onCreated(evaluacion.id)
          close()
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent data-testid="nueva-evaluacion-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.bateria.nuevaDialog.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bateria-psicologo">{t('admin.bateria.nuevaDialog.psicologo')}</Label>
            {psicologos.length === 0 ? (
              <NoPsicologosNotice testId="no-psicologos" />
            ) : (
              <PsicologoSelect
                id="bateria-psicologo"
                psicologos={psicologos}
                value={psicologoUserId}
                onChange={setPsicologoUserId}
                testId="bateria-psicologo"
              />
            )}
            <p className="text-xs text-muted-foreground">{t('admin.bateria.nuevaDialog.psicologoHint')}</p>
          </div>

          <DateField
            id="bateria-fecha"
            label={t('admin.bateria.nuevaDialog.fecha')}
            value={fecha}
            max={todayIso()}
            onChange={setFecha}
          />

          <div className="space-y-2">
            <Label htmlFor="bateria-notas">{t('admin.bateria.nuevaDialog.notas')}</Label>
            <Textarea
              id="bateria-notas"
              data-testid="bateria-notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              maxLength={2000}
              minRows={2}
            />
          </div>

          <FormDialogFooter
            error={error}
            pending={create.isPending}
            submitLabel={t('admin.bateria.nuevaDialog.submit')}
            onCancel={close}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
